import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

const defaultProps = {
    bodyClassName: '',
    lang: 'en',
    locale: 'en-GB',
    meta: [],
    pageDescription: '',
    pageSocialImage: '',
    pageTitle: '',
    pageTwitterCard: 'summary_large_image',
    siteAuthor: 'Oxbury',
    siteName: 'Oxbury',
    siteUrl: 'https://www.oxbury.com',
};

const propTypes = {
    bodyClassName: PropTypes.string,
    lang: PropTypes.string,
    locale: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.string),
    pageDescription: PropTypes.string,
    pageSocialImage: PropTypes.string,
    pageTitle: PropTypes.string,
    pageTwitterCard: PropTypes.string,
    pathname: PropTypes.string.isRequired,
    siteAuthor: PropTypes.string,
    siteName: PropTypes.string,
    siteUrl: PropTypes.string,
};

function useGlobalSEO() {
    const data = useStaticQuery(graphql`
        query GlobalSEO {
            allPrismicSite {
                nodes {
                    data {
                        site_meta_description {
                            text
                        }
                        site_meta_title {
                            text
                        }
                        site_social_share_image {
                            url
                        }
                    }
                }
            }
        }
    `);

    return data;
}

function SEO({
    bodyClassName,
    lang,
    locale,
    meta,
    pathname,
    pageDescription,
    pageSocialImage,
    pageTitle,
    pageTwitterCard,
    siteAuthor,
    siteName,
    siteUrl,
}) {
    const {
        allPrismicSite: {
            nodes
        }
    } = useGlobalSEO();
    const data = nodes?.[0]?.data;
    const siteDescription = data?.site_meta_description?.text;
    const siteSocialImage = data?.site_social_share_image?.url;
    const siteTitle = data?.site_meta_title?.text;

    const metaDescription = pageDescription || siteDescription;
    const metaSocialImage = pageSocialImage !== '' ? pageSocialImage : siteSocialImage;
    const metaTitle = pageTitle !== '' ? `${pageTitle} | ${siteTitle}` : siteTitle;

    return (
        <Helmet
            htmlAttributes={{ lang }}
            bodyAttributes={{ class: bodyClassName }}
            title={pageTitle}
            defaultTitle={siteTitle}
            titleTemplate={`%s | ${siteTitle}`}
            link={[
                {
                    crossorigin: true,
                    href: 'https://www.google-analytics.com',
                    rel: 'preconnect',
                },
                {
                    crossorigin: true,
                    href: 'https://code.jquery.com', // Loaded by Prismic preview, I think they’ve refactored this out but PR hasn’t been merged into gatsby-source-prismic-graphql yet
                    rel: 'preconnect',
                },
                {
                    crossorigin: true,
                    href: 'https://static.cdn.prismic.io',
                    rel: 'preconnect',
                },
                {
                    crossorigin: true,
                    href: 'https://oxbury-fs-plc.prismic.io',
                    rel: 'preconnect',
                },
            ]}
            meta={[
                {
                    name: 'description',
                    content: metaDescription,
                },
                {
                    name: 'article:author',
                    content: siteAuthor,
                },
                {
                    property: 'og:title',
                    content: metaTitle,
                },
                {
                    property: 'og:description',
                    content: metaDescription,
                },
                {
                    property: 'og:type',
                    content: 'website',
                },
                {
                    property: 'og:image',
                    content: metaSocialImage,
                },
                {
                    property: 'og:url',
                    content: `${siteUrl}${pathname}`,
                },
                {
                    property: 'og:locale',
                    content: locale,
                },
                {
                    property: 'og:site_name',
                    content: siteName,
                },
                {
                    name: 'twitter:card',
                    content: pageTwitterCard,
                },
                {
                    name: 'twitter:creator',
                    content: siteAuthor,
                },
                {
                    name: 'twitter:title',
                    content: metaTitle,
                },
                {
                    name: 'twitter:description',
                    content: metaDescription,
                },
                {
                    name: 'twitter:image',
                    content: metaSocialImage,
                },
                {
                    name: 'twitter:url',
                    content: `${siteUrl}${pathname}`,
                },
                {
                    name: 'apple-mobile-web-app-status-bar-style',
                    content: 'black-translucent',
                },
                {
                    name: 'google-site-verification',
                    content: '',
                },
            ].concat(meta)}
        />
    );
}

SEO.propTypes = propTypes;
SEO.defaultProps = defaultProps;

export default SEO;
